/**
 * Signature Stay (Comfort Plus) package enrichments are priced into the overnight
 * rate. They must not appear as à-la-carte charges or enter VIPMEMBER % bases.
 *
 * Pricing is per pet: compare the addon's pet_id to that pet's boarding_options.stay_type.
 * One dog on Signature Stay can have included enrichments at $0 while another dog on a
 * standard stay is charged list price for the same enrichment type.
 */

import { STAY_TYPES, VISIT_TYPES } from './constants'
import { STATUS_ID, LINE_ITEM_TYPES } from './enums'

const findFacilityAddonType = (addon, facility) => {
	const addonTypeId = (addon?.addon_id || addon?._id)?.toString()
	if (!addonTypeId || !facility?.add_on_services) {
		return null
	}

	const buckets = facility.add_on_services

	// Flat array shape (some client/facility payloads)
	if (Array.isArray(buckets)) {
		return buckets.find((t) => String(t._id) === addonTypeId) || null
	}

	if (addon.type && Array.isArray(buckets[addon.type])) {
		const byType = buckets[addon.type].find((t) => String(t._id) === addonTypeId)
		if (byType) return byType
	}

	for (const key of Object.keys(buckets)) {
		const list = buckets[key]
		if (!Array.isArray(list)) continue
		const match = list.find((t) => String(t._id) === addonTypeId)
		if (match) return match
	}

	return null
}

/** True when this addon's pet has the given stay_type on the visit. */
const addonPetHasStayType = (addon, visit, stayType) => {
	if (!addon?.pet_id || !visit?.boarding_options?.length) {
		return false
	}
	const petOption = visit.boarding_options.find(
		(o) => String(o.pet_id) === String(addon.pet_id)
	)
	return petOption?.stay_type === stayType
}

/**
 * Pet ids on the visit whose boarding_options stay_type matches.
 * Only pets still on the visit (pet_ids) count.
 *
 * @param {Object} visit
 * @param {string} stayType - a STAY_TYPES value
 * @returns {string[]}
 */
const petIdsWithStayType = (visit, stayType) => {
	const petIds = new Set((visit?.pet_ids || []).map(String))

	return (visit?.boarding_options || [])
		.filter((option) => option?.stay_type === stayType && petIds.has(String(option.pet_id)))
		.map((option) => String(option.pet_id))
}

const isComfortPlusEnrichmentType = (addon, facility) => {
	if (addon.included_in_comfort_plus || addon.comfort_plus_included) {
		return true
	}
	const addonType = findFacilityAddonType(addon, facility)
	return !!addonType?.comfort_plus_included
}

const isSignaturePlayEnrichmentType = (addon, facility) => {
	if (addon.included_in_signature_play || addon.signature_play_included) {
		return true
	}
	const addonType = findFacilityAddonType(addon, facility)
	return !!addonType?.signature_play_included
}

export const isSignatureStayPackageAddon = (addon, visit = null, facility = null) => {
	if (!addon) return false

	if (addonPetHasStayType(addon, visit, STAY_TYPES.SignaturePlay)) {
		return isSignaturePlayEnrichmentType(addon, facility)
	}

	if (!isComfortPlusEnrichmentType(addon, facility)) {
		return false
	}

	return addonPetHasStayType(addon, visit, STAY_TYPES.SignatureStay)
}

/** Facility offers Signature Stay when any boarding enrichment is bundled into the tier. */
export const facilityOffersSignatureStay = (facility) => {
	if (!facility) return false

	if (facility.services?.boarding?.comfort_plus_overnights_price) {
		return true
	}

	const types = facility.addOnTypes || []
	if (
		types.some(
			(t) => t.comfort_plus_included && t.available_boarding !== false && t.active !== false
		)
	) {
		return true
	}

	const buckets = facility.add_on_services
	if (!buckets) return false

	const list = Array.isArray(buckets)
		? buckets
		: Object.values(buckets).flatMap((group) => (Array.isArray(group) ? group : []))

	return list.some(
		(t) => t.comfort_plus_included && t.available_boarding !== false && t.active !== false
	)
}

/** Facility offers Signature Play when any active enrichment is bundled into it (same rule as the client app). */
export const facilityOffersSignaturePlay = (facility) => {
	if (!facility) return false

	const buckets = facility.add_on_services
	const bucketList = !buckets
		? []
		: Array.isArray(buckets)
			? buckets
			: Object.values(buckets).flatMap((group) => (Array.isArray(group) ? group : []))

	return [...(facility.addOnTypes || []), ...bucketList].some(
		(t) => t.signature_play_included && t.active !== false
	)
}

/**
 * True once a daycare visit has gone through checkout: credits were drawn, the daycare
 * line item was written, or any non-deposit line item reached Stripe. The unbilled
 * Signature Play / early / late rows that admin edits create do not count.
 */
export const visitHasBeenCharged = (visit) => {
	if (visit?.credits_used?.length) return true

	return (visit?.lineItems || []).some(
		(item) =>
			item.type !== LINE_ITEM_TYPES.Deposit &&
			(item.type === LINE_ITEM_TYPES.Daycare || !!item.stripe_charge_status)
	)
}

/** True when any pet on the daycare visit is booked on Signature Play. */
export const visitHasSignaturePlay = (visit) => {
	return visitHasPetOnStayType(visit, VISIT_TYPES.Daycare, STAY_TYPES.SignaturePlay)
}

export const visitHasSignatureStay = (visit) => {
	const petIds = visit?.pet_ids || []
	if (!petIds.length) return false
	const signatureStayPetIds = new Set(petIdsWithStayType(visit, STAY_TYPES.SignatureStay))
	return petIds.every((petId) => signatureStayPetIds.has(String(petId)))
}

const SCHEDULED_STATUS_IDS = [STATUS_ID.Approved, STATUS_ID.InProgress, STATUS_ID.Completed]

/**
 * True when the visit is of the given type and at least one of its pets is on the stay type.
 *
 * @param {Object} visit
 * @param {string} visitType - a VISIT_TYPES value
 * @param {string} stayType - a STAY_TYPES value
 * @returns {boolean}
 */
export const visitHasPetOnStayType = (visit, visitType, stayType) => {
	return visit?.type === visitType && petIdsWithStayType(visit, stayType).length > 0
}

const isScheduledVisitOfType = (visit, type) => {
	return visit?.type === type && SCHEDULED_STATUS_IDS.includes(visit?.status?.id)
}

/**
 * True when the pet has a Signature Stay enrichment scheduled on the date.
 */
const hasIncludedSignatureStayAddonOnDate = (visit, petId, date) => {
	return (visit?.add_on_services || []).some(
		(addon) =>
			addon?.included_in_comfort_plus === true &&
			String(addon.pet_id) === petId &&
			addon.date === date
	)
}

/**
 * Number of Signature Stay dogs on a date.
 * Middle nights always count. Drop-off and pick-up days only count when the
 * pet has an included enrichment scheduled that day.
 *
 * @param {Object[]} visits - schedule visits (with add_on_services attached)
 * @param {string} date - 'YYYY-MM-DD'
 * @returns {number}
 */
export const countSignatureStayPetsOnDate = (visits = [], date) => {
	return visits.reduce((total, visit) => {
		if (!isScheduledVisitOfType(visit, VISIT_TYPES.Boarding)) {
			return total
		}

		const dates = visit.dates || []
		if (!dates.includes(date)) {
			return total
		}

		const isEdgeDate = date === dates[0] || date === dates[dates.length - 1]
		const signatureStayPetIds = petIdsWithStayType(visit, STAY_TYPES.SignatureStay)
		const countedPetIds = isEdgeDate
			? signatureStayPetIds.filter((petId) => hasIncludedSignatureStayAddonOnDate(visit, petId, date))
			: signatureStayPetIds

		return total + countedPetIds.length
	}, 0)
}

/**
 * Number of Signature Play dogs in daycare on a date.
 *
 * @param {Object[]} visits - schedule visits
 * @param {string} date - 'YYYY-MM-DD'
 * @returns {number}
 */
export const countSignaturePlayPetsOnDate = (visits = [], date) => {
	return visits.reduce((total, visit) => {
		if (!isScheduledVisitOfType(visit, VISIT_TYPES.Daycare) || !(visit.dates || []).includes(date)) {
			return total
		}

		const signaturePlayPetIds = petIdsWithStayType(visit, STAY_TYPES.SignaturePlay)

		return total + signaturePlayPetIds.length
	}, 0)
}

/** Daycare boarding_options with stay_type applied to every pet on the visit (no kennel). */
export const daycareOptionsWithStayType = (visit, stayType) => {
	const existing = visit?.boarding_options || []
	return (visit?.pet_ids || []).map((petId) => {
		const prior = existing.find((o) => String(o.pet_id) === String(petId)) || {}
		return { ...prior, pet_id: petId, stay_type: stayType }
	})
}

/** Build boarding_options with stay_type applied to every pet on the visit. */
export const boardingOptionsWithStayType = (visit, stayType) => {
	const petIds = visit?.pet_ids || []
	const existing = visit?.boarding_options || []
	return petIds.map((petId) => {
		const prior = existing.find((o) => String(o.pet_id) === String(petId)) || {}
		return {
			...prior,
			pet_id: petId,
			kennel_type: prior.kennel_type || 'Standard',
			stay_type: stayType
		}
	})
}
